import React, { Component } from 'react';
import ModalModule from '../modal';
import UI from '../ui';
import Typography from '../ui/Typography.component';
import Utils from '../utils';

const { Modal, ModalButtonActionType } = ModalModule;
const { WrappedInputTextWithLabel, Link, WrappedButton, WrappedButtonType } =
	UI;
const { Logger, Communication, withLayoutAndModalContexts, Util } = Utils;

class ForgotPassword extends Component {
	constructor(props) {
		super(props);

		this.state = {
			username: Util.getQueryParamByName('username') || '',
			usernameError: 0,
			showSuccess: false,
			showEmailLinkedMessage: false,
			linkedEmail: '',
		};

		Util.removeQueryParamByName(['username']);
	}

	handleInputChange = e => {
		this.setState({
			[e.target.name]: e.target.value,
		});
	};

	onInputTextBlur = e => {
		const { name, value } = e.target;
		const fieldError = `${name}Error`;

		this.setState({
			[fieldError]: value !== '' ? 1 : -1,
		});
	};

	onKeyPressEnter = e => {
		const {
			key,
			target: { value },
		} = e;

		if (key === 'Enter' && value !== '') {
			e.preventDefault();
			this.submitPasswordReset();
		}
	};

	hasInputFieldError = () => {
		const { usernameError } = this.state;

		return usernameError !== -1;
	};

	displayError = () => {
		const {
			modalContext: { showModal, closeModal },
		} = this.props;

		showModal({
			component: ({ modalId }) => (
				<Modal
					className="common-authentication"
					modalId={modalId}
					header="Forgot Password"
					primaryButton={{
						type: ModalButtonActionType.Ok,
						text: 'Close',
						onClick: () => {
							closeModal(modalId);
						},
					}}
				>
					<p>We were unable to process your password reset request.</p>
					<br />
					<p>
						Please try again, or for further assistance, read our{' '}
						<Link
							className="no-text-decoration"
							dataqa="reset-password-help-link"
							onClick={() =>
								Util.openHelpCenter(
									`20821907151899#UUID-acad6603-3116-300f-ad8d-e1584c41b34a_UUID-1cf6112d-8597-27ab-d4a0-ef7e1b965504`
								)
							}
						>
							reset password help article
						</Link>
						.
					</p>
				</Modal>
			),
		});
	};

	submitPasswordReset = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { username } = this.state;

		Logger.saveActionData('Forgot Password Button Clicked');

		// validate username
		if (this.hasInputFieldError()) {
			// unacceptable characters are &, #, and whitespace (Same as WebReg username validation)
			const hasSpecialChar = username.match('[&#\\s]+');

			if (hasSpecialChar && hasSpecialChar.length > 0) {
				this.displayError();
			} else {
				const startPasswordResetResponse =
					await Communication.startPasswordReset({
						Username: username,
						Codeword1: '',
						Codeword2: '',
					});

				if (Communication.isSuccessful(startPasswordResetResponse)) {
					this.setState({
						showSuccess: true,
						showEmailLinkedMessage: false,
						linkedEmail: '',
					});
				} else {
					const errorDescription =
						startPasswordResetResponse.ErrorDescription || '';
					const hasEmailLinked = errorDescription
						.toLowerCase()
						.includes('multiple accounts linked to the provided email address');

					if (hasEmailLinked) {
						// Extract email from error description if available
						const emailMatch = errorDescription.match(/[\w.-]+@[\w.-]+\.\w+/);
						const extractedEmail = emailMatch ? emailMatch[0] : '';

						this.setState({
							showEmailLinkedMessage: true,
							linkedEmail: extractedEmail,
						});
					} else {
						this.setState({
							showEmailLinkedMessage: false,
							linkedEmail: '',
						});

						showError({
							data: {
								title: 'Error',
								description: startPasswordResetResponse.ErrorDescription,
								primaryButtonType: ModalButtonActionType.Ok,
								primaryButtonText: 'OK',
								handleType: 'custom',
							},
						});
					}
				}
			}
		}
	};

	resendEmail = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { username } = this.state;

		const startPasswordResetResponse = await Communication.startPasswordReset({
			Username: username,
			Codeword1: '', // not used for password reset
			Codeword2: '', // not used for password reset
		});

		if (!Communication.isSuccessful(startPasswordResetResponse)) {
			showError({
				data: {
					title: 'Error',
					description: startPasswordResetResponse.ErrorDescription,
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
			});
		}
	};

	onForgotUsernameLinkClick = () => {
		const { handleForgotUsernameLinkClick } = this.props;
		handleForgotUsernameLinkClick();
	};

	onPasswordChangeSuccess = () => {
		// redirect to Auth0 login page
		const { goToSignInView } = this.props;
		goToSignInView();
	};

	goToSignInView = () => {
		const { goToSignInView } = this.props;
		goToSignInView();
	};

	renderSuccessMessage = () => {
		const { isAuth0Winclient, isAuth0Mobile } = this.props;

		if (isAuth0Winclient || isAuth0Mobile) {
			const appContext = isAuth0Winclient
				? 'Stamps.com application'
				: 'mobile app';
			return (
				<>
					<p className="content">
						We've sent an email to the address associated with your account
						(check your spam folder if you don't see it).
					</p>

					<p className="content">
						First, close this {appContext}, then click the link provided in the
						email. You'll be prompted to create a new password for your account.
						You can then launch the {appContext} and log in with your new
						password.
					</p>
				</>
			);
		}

		return (
			<>
				<div className="primary-header">Check Your Email</div>
				<p className="content">
					We will email a password recovery link to the email address connected
					to your account.
				</p>

				<p className="content">
					Check your spam folder if you cannot find the email after a few
					minutes.
				</p>
			</>
		);
	};

	render() {
		const {
			username,
			usernameError,
			showSuccess,
			showEmailLinkedMessage,
			linkedEmail,
		} = this.state;

		const hasContent = str => str && str.trim().length > 0;

		if (showSuccess) {
			return (
				<div className="common-authentication auth-forgot-password-view success-message-view">
					<div className="form">
						{this.renderSuccessMessage()}

						<p className="content">
							For further help, visit our{' '}
							<Link
								className="no-text-decoration reset-password-help-link"
								dataqa="reset-password-help-link"
								onClick={() =>
									Util.openHelpCenter(
										`20821907151899#UUID-acad6603-3116-300f-ad8d-e1584c41b34a_UUID-1cf6112d-8597-27ab-d4a0-ef7e1b965504`
									)
								}
							>
								password reset help article
							</Link>{' '}
						</p>
						<div className="row-container centered">
							<WrappedButton
								label="Return to Login"
								buttonType={WrappedButtonType.Primary}
								dataqa="return-to-success-message"
								onClick={this.onPasswordChangeSuccess}
							/>
						</div>
						<div className="row-container centered">
							<WrappedButton
								label="Resend email"
								buttonType={WrappedButtonType.Secondary}
								dataqa="resend-email-success-message"
								onClick={this.resendEmail}
							/>
						</div>
					</div>
				</div>
			);
		} else {
			return (
				<div className="common-authentication auth-forgot-password-view">
					<div className="form">
						<div className="primary-header">Reset Password</div>
						<p className="content">Enter your Stamps.com account username.</p>
						{showEmailLinkedMessage && (
							<Typography.P className="content">
								You have multiple accounts linked to the email address provided:{' '}
								{username}. Please enter the username for the correct account
								below.
							</Typography.P>
						)}
						<div className="row-container">
							<WrappedInputTextWithLabel
								showErrorBelow
								isInputInvalid={usernameError === -1}
								errorMessage="This field is required"
								className="inputtext"
								label={
									showEmailLinkedMessage ? 'Username' : 'Email or Username'
								}
								uiElementConfig={{
									type: 'text',
									name: 'username',
									value: username,
									onChange: this.handleInputChange,
									onBlur: this.onInputTextBlur,
									onKeyPress: this.onKeyPressEnter,
									required: true,
									dataqa: 'forgotpasswordusername',
									autoFocus: true,
								}}
							/>
							<p className="username-link">
								<Link
									onClick={this.onForgotUsernameLinkClick}
									dataqa="forgot-username-link"
								>
									Forgot username?
								</Link>
							</p>
						</div>

						<div className="row-container centered">
							<WrappedButton
								label="Continue"
								buttonType={WrappedButtonType.Primary}
								dataqa="continue-forgot-password"
								onClick={this.submitPasswordReset}
								disabled={!hasContent(username)}
							/>
						</div>
						<div className="row-container centered">
							<WrappedButton
								label="I forgot my username"
								buttonType={WrappedButtonType.Secondary}
								dataqa="forgot-username-button"
								onClick={this.onForgotUsernameLinkClick}
							/>
						</div>
						<div className="row-container centered">
							<WrappedButton
								label="Cancel"
								buttonType={WrappedButtonType.TertiaryGhost}
								dataqa="cancel-forgot-password"
								onClick={this.goToSignInView}
							/>
						</div>
					</div>
				</div>
			);
		}
	}
}

export default withLayoutAndModalContexts(ForgotPassword);
